import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { CustomerBo } from '@utils/types/back-office/customer-bo.ts';
import { OptionType } from '@components/shared/types.ts';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import PageHeader from '@components/layout/page-header.tsx';
import { PublishingPolicyCard } from './publishing-policy-card';

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<CustomerBo, any>;
  errors: FieldErrors<CustomerBo>;
  customer?: CustomerBo;
  customerId?: string;
  cardStyle: React.CSSProperties;
}

export function CustomerSettingsTab({
  control,
  errors,
  customer,
  customerId,
  cardStyle
}: Props) {
  const { t } = useTranslation();
  const [isReseller, setIsReseller] = useState(false);

  useEffect(() => {
    if (customer?.is_reseller !== undefined) {
      setIsReseller(customer.is_reseller);
    }
  }, [customer]);

  const resellerTranscriptAccessOptions: OptionType[] = [
    { label: t('list'), value: 0 },
    { label: t('read-only'), value: 1 },
    { label: t('write'), value: 2 }
  ];

  return (
    <div className="space-y-4 py-4">
      <div
        className="rounded-2xl border shadow-card p-4 sm:p-6"
        style={cardStyle}
      >
        <PageHeader
          title={t('account-settings')}
          className="border-b-0 mb-4"
          contentClassName="px-0 py-0"
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Controller
            control={control}
            name="is_active"
            defaultValue={!!customer?.is_active}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="is_active"
                name="is_active"
                label={t('activate-the-customer-account-on-create')}
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                variant="primary"
              />
            )}
          />
          <Controller
            control={control}
            name="upload_suspended"
            defaultValue={!!customer?.upload_suspended}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="upload_suspended"
                name="upload_suspended"
                label={t('suspend-uploading')}
                description={t('suspend-uploading-description')}
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                variant="primary"
              />
            )}
          />
          <Controller
            control={control}
            name="is_reseller"
            defaultValue={!!customer?.is_reseller}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="is_reseller"
                name="is_reseller"
                label={t('reseller')}
                checked={!!value}
                onChange={e => {
                  onChange(e.target.checked);
                  setIsReseller(e.target.checked);
                }}
                variant="primary"
              />
            )}
          />
          <Controller
            control={control}
            name="receive_invoice_by_email"
            defaultValue={!!customer?.receive_invoice_by_email}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="receive_invoice_by_email"
                name="receive_invoice_by_email"
                label={t('invoice-by-email')}
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                variant="primary"
              />
            )}
          />
          <Controller
            control={control}
            name="use_data_consent"
            defaultValue={!!customer?.use_data_consent}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="use_data_consent"
                name="use_data_consent"
                label={t('data-consent-usage')}
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                variant="primary"
              />
            )}
          />
          <Controller
            control={control}
            name="diarization"
            defaultValue={!!customer?.diarization}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="diarization"
                name="diarization"
                label={t('separate-speakers')}
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                variant="primary"
              />
            )}
          />
          <Controller
            control={control}
            name="use_plain_document"
            defaultValue={!!customer?.use_plain_document}
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="use_plain_document"
                name="use_plain_document"
                label={t('plain-document-translate')}
                checked={!!value}
                onChange={e => onChange(e.target.checked)}
                variant="primary"
              />
            )}
          />
        </div>
      </div>

      <div
        className="rounded-2xl border shadow-card p-4 sm:p-6"
        style={cardStyle}
      >
        <PageHeader
          title={t('default-integration-to-use-for-this-customer')}
          className="border-b-0 mb-4"
          contentClassName="px-0 py-0"
        />
        <Controller
          control={control}
          name="default_integration_id"
          defaultValue={customer?.default_integration_id || 2}
          render={({ field: { onChange, value } }) => (
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  value={1}
                  checked={value === 1}
                  onChange={() => onChange(1)}
                  className="h-4 w-4 border-hairline text-primary focus:ring-primary"
                />
                <span className="font-medium">Kaldi</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  value={2}
                  checked={value === 2}
                  onChange={() => onChange(2)}
                  className="h-4 w-4 border-hairline text-primary focus:ring-primary"
                />
                <span className="font-medium">Whisper</span>
              </label>
            </div>
          )}
        />
        {errors.default_integration_id && (
          <p className="text-red-500 text-xs mt-2">
            {errors.default_integration_id.message}
          </p>
        )}
      </div>

      {customerId && (
        <PublishingPolicyCard
          customerId={customerId}
          cardStyle={cardStyle}
          isReseller={isReseller}
        />
      )}

      {isReseller && (
        <div
          className="rounded-2xl border shadow-card p-4 sm:p-6"
          style={cardStyle}
        >
          <PageHeader
            title={t('object-access')}
            className="border-b-0 mb-4"
            contentClassName="px-0 py-0"
          />
          <Controller
            control={control}
            name="reseller_transcript_access"
            defaultValue={customer?.reseller_transcript_access ?? 0}
            render={({ field: { onChange, value } }) => (
              <div className="flex items-center gap-6">
                {resellerTranscriptAccessOptions.map(option => (
                  <label
                    key={option.value}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <input
                      type="radio"
                      value={option.value}
                      checked={value === option.value}
                      onChange={() => onChange(option.value)}
                      className="h-4 w-4 border-hairline text-primary focus:ring-primary"
                    />
                    <span className="font-medium">{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          />
          {errors.reseller_transcript_access && (
            <p className="text-red-500 text-xs mt-2">
              {errors.reseller_transcript_access.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
